<template>
  <v-card
    elevation="0"
    :loading="typeof participants === 'undefined'"
    color="transparent"
  >
    <v-expansion-panels
      popout
      v-if="participants"
    >
      <v-expansion-panel
        v-for="(participant) in participants.collection"
        :key="participant.id"
      >
        <v-expansion-panel-header>
          <v-row dense>
            <v-col cols="4">
              <template v-if="participant.name !== null">
                {{ participant.name }}
              </template>
              <template v-else>
                {{ participant.customer.profile.firstName }} {{ participant.customer.profile.lastName }}
              </template>
            </v-col>
            <v-col cols="6">
              {{ participant.seminar.number }} {{ participant.seminar.name }}
            </v-col>
            <v-col cols="2">
              <div class="text-end pr-6">
                {{ formatDateTime(isoToDateObject(participant.createdAt)) }}
              </div>
            </v-col>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row>
            <v-col cols="12" lg="6">
              <v-card color="mainAccent lighten-1">
                <v-card-title>Seminar</v-card-title>
                <v-card-text>
                  <v-simple-table dense>
                    <tbody>
                      <tr>
                        <th>Name:</th>
                        <td>{{ participant.seminar.name }}</td>
                      </tr>
                      <tr>
                        <th>Nummer:</th>
                        <td>{{ participant.seminar.number }}</td>
                      </tr>
                      <tr>
                        <th>Start:</th>
                        <td>{{ formatDateTime(isoToDateObject(participant.seminar.timeSlot.start)) }} Uhr</td>
                      </tr>
                      <tr>
                        <th>Referent/in:</th>
                        <td>
                          {{ participant.seminar.speaker.profile.firstName }}
                          {{ participant.seminar.speaker.profile.lastName }}
                        </td>
                      </tr>
                      <tr>
                        <th>Gebühr:</th>
                        <td>{{ showEuroCurrency(participant.seminar.fee) }}</td>
                      </tr>
                      <tr>
                        <th>
                          {{ $t(currentSeminarSettings['hoursLabel'] === 'hours' ? 'timeHours' : 'teachingUnits') }}:
                        </th>
                        <td>{{ participant.seminar.teachingUnits }}</td>
                      </tr>
                      <tr>
                        <th>Bestätigt:</th>
                        <td>{{ participant.seminar.seminarConfig.confirmed ? 'Ja' : 'Nein' }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    color="primary"
                    :to="{ name: 'SeminarShow', params: { id: participant.seminar.id } }"
                    style="margin-left: auto;"
                  >
                    zum Seminar
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
            <v-col cols="12" lg="6">
              <v-card color="mainAccent lighten-1">
                <v-card-title>Teilnehmer/in</v-card-title>
                <v-card-text>
                  <v-simple-table dense>
                    <tbody>
                      <tr>
                        <th>Name:</th>
                        <td>
                          <template v-if="participant.name !== null">
                            {{ participant.name }}
                          </template>
                          <template v-else>
                            {{ participant.customer.profile.firstName }} {{ participant.customer.profile.lastName }}
                          </template>
                        </td>
                      </tr>
                      <tr v-if="participant.customer">
                        <th>KundenNr.:</th>
                        <td>{{ participant.customer.customerNumber }}</td>
                      </tr>
                      <tr>
                        <th>Geschäftsaccount:</th>
                        <td>{{ participant.customer && participant.customer.isBusiness ? 'Ja' : 'Nein' }}</td>
                      </tr>
                      <tr>
                        <th>Eingeladen:</th>
                        <td>
                          {{ participant.invited ? 'Ja' : 'Nein' }}
                        </td>
                      </tr>
                      <tr>
                        <th>Teilgenommen: </th>
                        <td>{{ participant.participated ? 'Ja' : 'Nein' }}</td>
                      </tr>
                      <tr>
                        <th>Kommentar: </th>
                        <td>{{ participant.comment ? participant.comment : '' }}</td>
                      </tr>
                      <tr>
                        <th>Rechnung: </th>
                        <td>
                          <template v-if="participant.invoices.edges.length > 0">
                            <div
                              v-for="(invoiceEdge) in participant.invoices.edges"
                              :key="invoiceEdge.node.id"
                            >
                              <v-btn
                                :to="{ name: 'InvoiceShow', params: { id: invoiceEdge.node.id } }"
                                text
                                small
                                plain
                                style="text-transform: none; padding: 0"
                              >
                                #{{ invoiceEdge.node.number }} -&nbsp;
                                <span v-if="invoiceEdge.node.paid">bezahlt</span>
                                <span v-else>offen</span>
                              </v-btn>
                            </div>
                          </template>
                          <template v-else>
                            keine Rechnung
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    color="primary"
                    :to="{ name: 'CustomerShow', params: { id: participant.customer.id } }"
                    style="margin-left: auto;"
                    v-if="participant.customer"
                  >
                    zum Kunden
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-skeleton-loader
      v-else
      type="list-item, list-item, list-item, list-item"
    ></v-skeleton-loader>
  </v-card>

</template>

<script>
  import gql from 'graphql-tag'
  import AuthenticationMixin from '../../mixins/AuthenticationMixin';
  import { formatDateTime, isoToDateObject } from '../../utils/dates';
  import { showEuroCurrency } from '../../utils/money';
  import TenantMixin from '../../mixins/TenantMixin';

  export default {
    name: 'LastBookingsListing',
    mixins: [AuthenticationMixin, TenantMixin],
    props: {
      amount: {
        type: Number,
        default: 10
      },
    },
    data: () => ({}),
    methods: {
      formatDateTime(date) {
        return formatDateTime(date, 'DD.MM. HH:mm');
      },
      isoToDateObject,
      showEuroCurrency,
    },
    apollo: {
      participants() {
        return {
          query: gql`
            query participants($itemsPerPage: Int) {
              participants(
                itemsPerPage: $itemsPerPage
                order: [{ createdAt: "DESC" }]
              ){
                collection {
                  id
                  name
                  email
                  customer{
                    id
                    customerNumber
                    isBusiness
                    profile{
                      id
                      firstName
                      lastName
                      company
                    }
                  }
                  seminar{
                    id
                    slug
                    name
                    number
                    teachingUnits
                    participantCount
                    fee
                    timeSlot{
                      start
                      end
                    }
                    speaker{
                      id
                      profile{
                        id
                        firstName
                        lastName
                      }
                    }
                    seminarConfig{
                      id
                      confirmed
                    }
                  }
                  guest
                  guestProfile{
                    id
                    firstName
                    lastName
                    company
                  }
                  invited
                  participated
                  comment
                  invoices{
                    edges{
                      node{
                        id
                        number
                        paid
                        frozenTotalNet
                        frozenTotalGross
                      }
                    }
                  }
                  createdAt
                }
              }
            }
          `,
          variables() {
            return {
              itemsPerPage: this.amount
            }
          },
          context: {
            headers: {
              authorization: this.$store.state.authentication.loggedIn ? `Bearer ${this.$store.state.authentication.token}` : ''
            }
          }
        }
      },
    },
  };
</script>
